/// <reference types="vite/client" />

export interface QuestionAffixes {
  suffixes: string[];
  prefixes: string[];
  prefixAlphabet: string[];
}

const rawFiles = import.meta.glob('./alphabet-questions/**/*.txt', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function normalizeLanguageCode(language: string): string {
  const lang = language.trim().toLowerCase();
  if (!lang) return 'en';
  const dash = lang.indexOf('-');
  const underscore = lang.indexOf('_');
  const cut = [dash, underscore].filter((i) => i > 0).sort((a, b) => a - b)[0];
  return cut !== undefined ? lang.slice(0, cut) : lang;
}

function readLines(folder: string, lang: string): string[] {
  const primaryKey = `./alphabet-questions/${folder}/${lang}.txt`;
  const fallbackKey = `./alphabet-questions/${folder}/en.txt`;
  const raw = rawFiles[primaryKey] ?? rawFiles[fallbackKey] ?? '';
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function getQuestionAffixesForLanguage(language: string): QuestionAffixes {
  const lang = normalizeLanguageCode(language);
  return {
    suffixes: readLines('suffix', lang),
    prefixes: readLines('preffix', lang),
    prefixAlphabet: readLines('preffix_alphabet', lang),
  };
}

export function buildQuestionQueries(
  seed: string,
  affixes: QuestionAffixes,
  alphabet: string[],
  maxQueries: number,
): string[] {
  const trimmed = seed.trim();
  if (!trimmed) return [];

  const queries: string[] = [];
  const limitReached = () => maxQueries > 0 && queries.length >= maxQueries;

  for (const suffix of affixes.suffixes) {
    if (limitReached()) return queries;
    queries.push(`${trimmed} ${suffix}`);
  }
  for (const prefix of affixes.prefixes) {
    if (limitReached()) return queries;
    queries.push(`${prefix} ${trimmed}`);
  }
  for (const prefix of affixes.prefixAlphabet) {
    for (const letter of alphabet) {
      if (limitReached()) return queries;
      queries.push(`${prefix} ${trimmed} ${letter}`);
    }
  }

  return queries;
}
